/**
 * Visao geral: o CRM que cada empresa ve ao entrar (RNF15 / RNF12).
 *
 * Hierarquia, de cima para baixo:
 *  1. Faixa de identidade da empresa com o TOTAL do periodo (retorno, nunca
 *     "gasto"): liquido em destaque, comissao explicada, bruto, vendas,
 *     ticket, taxa -- cada um com a variacao contra o periodo anterior.
 *  2. "Agora": o motor neste instante (janela de 30 min, fila, ligacoes,
 *     respostas, comercial). Retrato ao vivo, fora do filtro de periodo.
 *  3. Uma linha por funil habilitado (`company.funnels`), com as mesmas seis
 *     medidas, sinal de saude, proximo passo, responsavel e atalho.
 *  4. "O que fazer agora" (insights derivados dos numeros) e a projecao do
 *     mes no ritmo atual, lado a lado.
 *  5. Receita recuperada por dia/semana/mes e a divisao por canal.
 *  6. Ranking por oferta (ordenavel, com busca e "ver mais").
 *  7. Tendencia diaria e funil de conversao, lado a lado.
 *  8. Atividade recente.
 *
 * Decisoes nao obvias:
 *  - Seis recursos independentes (resumo, agora, metricas, serie, recentes,
 *    equipe), cada um com seu proprio loading/erro: a falha de UMA rota nova
 *    (/api/funnels/summary ainda nao existe) nao pode derrubar a tendencia e
 *    o funil, que ja funcionam no backend.
 *  - `company.id` entra nas dependencias de TODOS os fetchers, mesmo dos que
 *    nao o enviam: quando a administracao troca a empresa em foco, a tela
 *    inteira recarrega em vez de misturar numeros de duas empresas.
 *  - O responsavel de cada funil e estado LOCAL da previa, guardado por
 *    `${empresa}:${funil}` para uma troca de empresa nao herdar a escolha da
 *    outra. Ele entra no diagnostico e nos insights: designar alguem faz o
 *    "proximo passo" mudar na hora.
 *  - Toda leitura parte de um fato ja ocorrido (abandono ha 30 min, plano
 *    vencido). Nao existe bloco de "vai vencer" nem lembrete pre-vencimento:
 *    sem o fato nao ha recuperacao e, portanto, nao ha comissao.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { FunnelChart, type FunnelStageView } from '../../../components/FunnelChart';
import { ErrorPanel, SimulatedNotice } from '../../../components/PageState';
import { TrendChart } from '../../../components/TrendChart';
import { fetchCampaigns, fetchDaily, fetchMetrics, useApiResource } from '../../../lib/api';
import { useCompany } from '../../../lib/companyContext';
import { formatDate } from '../../../lib/format';
import { describeSelection, rangeDays } from '../../../lib/period';
import { usePeriod } from '../../../lib/periodContext';
import type { DashboardMetrics, FunnelKind } from '../../../types';
import { fetchAgora, fetchEquipe, fetchResumoFunis } from '../api';
import { AtividadeRecente } from '../components/AtividadeRecente';
import { DestaquesPorOferta } from '../components/DestaquesPorOferta';
import { FaixaEmpresa } from '../components/FaixaEmpresa';
import { LinhaFunil } from '../components/LinhaFunil';
import { MotorAgora } from '../components/MotorAgora';
import { OQueFazerAgora } from '../components/OQueFazerAgora';
import { PrimeirosPassos } from '../components/PrimeirosPassos';
import { ProjecaoDoMes } from '../components/ProjecaoDoMes';
import { ReceitaPorCanal } from '../components/ReceitaPorCanal';
import { ReceitaRecuperada } from '../components/ReceitaRecuperada';
import { gerarInsights } from '../lib/insights';
import { diagnosticar } from '../lib/saude';

const SALVO_VISIVEL_MS = 1_800;

export function VisaoGeral(): JSX.Element {
  const { company } = useCompany();
  const { range, selection } = usePeriod();

  useEffect(() => {
    document.title = `Visão geral — ${company.name} · Converte Fácil`;
  }, [company.name]);

  const buscarResumo = useCallback((signal: AbortSignal) => fetchResumoFunis(company.id, range, signal), [company.id, range]);
  const buscarAgora = useCallback((signal: AbortSignal) => fetchAgora(company.id, signal), [company.id]);
  const buscarEquipe = useCallback((signal: AbortSignal) => fetchEquipe(company.id, signal), [company.id]);
  // `company.id` nas dependencias forca o refetch na troca de empresa (ver cabecalho).
  const buscarMetricas = useCallback((signal: AbortSignal) => fetchMetrics(range, signal), [range, company.id]);
  const buscarSerie = useCallback((signal: AbortSignal) => fetchDaily(range, signal), [range, company.id]);
  const buscarRecentes = useCallback((signal: AbortSignal) => fetchCampaigns(range, { limit: 6, offset: 0 }, signal), [range, company.id]);

  const resumo = useApiResource(buscarResumo, 30_000);
  const agora = useApiResource(buscarAgora, 15_000);
  const equipe = useApiResource(buscarEquipe);
  const metricas = useApiResource(buscarMetricas, 30_000);
  const serie = useApiResource(buscarSerie, 30_000);
  const recentes = useApiResource(buscarRecentes, 60_000);

  // Responsavel por funil: estado local da previa (ver cabecalho).
  const [responsaveis, setResponsaveis] = useState<Readonly<Record<string, string | null>>>({});
  const [salvoEm, setSalvoEm] = useState<string | null>(null);
  useEffect(() => {
    if (salvoEm === null) {
      return undefined;
    }
    const timer = window.setTimeout(() => setSalvoEm(null), SALVO_VISIVEL_MS);
    return () => window.clearTimeout(timer);
  }, [salvoEm]);

  const chaveDe = (kind: FunnelKind): string => `${company.id}:${kind}`;
  const definirResponsavel = (kind: FunnelKind, id: string | null): void => {
    const chave = chaveDe(kind);
    setResponsaveis((anterior) => ({ ...anterior, [chave]: id }));
    setSalvoEm(chave);
  };

  const resumoDados = resumo.state.status === 'success' ? resumo.state.data : null;
  const agoraDados = agora.state.status === 'success' ? agora.state.data : null;

  // Escolha local vence o que veio do servidor; sem escolha, vale o `assigneeId`.
  const responsaveisEfetivos = useMemo(() => {
    const mapa: Partial<Record<FunnelKind, string | null>> = {};
    for (const kind of company.funnels) {
      const escolhido = responsaveis[`${company.id}:${kind}`];
      mapa[kind] = escolhido !== undefined ? escolhido : resumoDados?.funnels.find((f) => f.kind === kind)?.assigneeId ?? null;
    }
    return mapa;
  }, [company.id, company.funnels, responsaveis, resumoDados]);

  const diagnosticos = useMemo(
    () =>
      resumoDados === null
        ? []
        : company.funnels.map((kind) => diagnosticar(resumoDados.funnels.find((f) => f.kind === kind) ?? null, kind, responsaveisEfetivos[kind] ?? null)),
    [company.funnels, resumoDados, responsaveisEfetivos],
  );

  const insights = useMemo(() => (resumoDados === null ? [] : gerarInsights(resumoDados, agoraDados, responsaveisEfetivos)), [resumoDados, agoraDados, responsaveisEfetivos]);

  const atualizando =
    (resumo.state.status === 'success' && resumo.state.refreshing) ||
    (metricas.state.status === 'success' && metricas.state.refreshing) ||
    (serie.state.status === 'success' && serie.state.refreshing);

  const simulado =
    (resumo.state.status === 'success' && resumo.state.simulated) ||
    (metricas.state.status === 'success' && metricas.state.simulated) ||
    (agora.state.status === 'success' && agora.state.simulated);

  const primeirosPassos = metricas.state.status === 'success' && metricas.state.data.abandonedCount === 0 && range.from === null;

  const dias = rangeDays(range);
  const membros = equipe.state.status === 'success' ? equipe.state.data : equipe.state.status === 'error' ? [] : null;

  const periodo =
    resumoDados !== null
      ? { inicio: resumoDados.periodStart, fim: resumoDados.periodEnd }
      : metricas.state.status === 'success'
        ? { inicio: metricas.state.data.periodStart, fim: metricas.state.data.periodEnd }
        : null;

  return (
    <section aria-labelledby="titulo-da-pagina" className="flex flex-col gap-6">
      <FaixaEmpresa
        company={company}
        periodo={describeSelection(selection)}
        estado={resumo.state}
        onRetry={resumo.reload}
        atualizando={atualizando}
        diagnosticos={diagnosticos}
      />

      {simulado ? <SimulatedNotice>Esta tela mostra um conjunto fictício e fixo. Nenhum número aqui vem do banco.</SimulatedNotice> : null}

      <MotorAgora estado={agora.state} onRetry={agora.reload} />

      {primeirosPassos ? (
        <PrimeirosPassos companyId={company.id} />
      ) : (
        <>
          <section aria-labelledby="titulo-funis" className="flex flex-col gap-3">
            <h2 id="titulo-funis" className="sr-only">
              Funis da empresa
            </h2>
            {resumo.state.status === 'loading' ? (
              <>
                <p role="status" className="sr-only">
                  Carregando os funis
                </p>
                <ul aria-hidden="true" className="flex flex-col gap-4">
                  {company.funnels.map((kind) => (
                    <li key={kind} className="h-56 animate-pulse rounded-2xl border border-line bg-surface" />
                  ))}
                </ul>
              </>
            ) : null}
            {resumo.state.status === 'error' ? (
              <ErrorPanel title="Não foi possível carregar os funis" error={resumo.state.error} onRetry={resumo.reload} />
            ) : null}
            {resumoDados !== null ? (
              <ul className="flex flex-col gap-4">
                {company.funnels.map((kind, i) => {
                  const dados = resumoDados.funnels.find((f) => f.kind === kind) ?? null;
                  const chave = chaveDe(kind);
                  const diagnostico = diagnosticos[i];
                  if (diagnostico === undefined) {
                    return null;
                  }
                  return (
                    <LinhaFunil
                      key={kind}
                      kind={kind}
                      resumo={dados}
                      feeBps={resumoDados.feeBps}
                      equipe={membros}
                      responsavelId={responsaveisEfetivos[kind] ?? null}
                      onResponsavel={(id) => definirResponsavel(kind, id)}
                      salvo={salvoEm === chave}
                      diagnostico={diagnostico}
                    />
                  );
                })}
              </ul>
            ) : null}
          </section>

          <div className="grid gap-6 lg:grid-cols-3">
            <OQueFazerAgora insights={insights} carregando={resumo.state.status === 'loading'} className="lg:col-span-2" />
            <ProjecaoDoMes resumo={resumo.state} onRetry={resumo.reload} companyId={company.id} />
          </div>

          <div className="grid gap-6 lg:grid-cols-3">
            <ReceitaRecuperada serie={serie.state} onRetry={serie.reload} dias={dias} className="lg:col-span-2" />
            <ReceitaPorCanal resumo={resumo.state} onRetry={resumo.reload} />
          </div>

          <DestaquesPorOferta resumo={resumo.state} onRetry={resumo.reload} funis={company.funnels} />

          <div className="grid gap-6 lg:grid-cols-3">
            <div className="lg:col-span-2">
              {serie.state.status === 'loading' ? <div aria-hidden="true" className="h-96 animate-pulse rounded-2xl border border-line bg-surface" /> : null}
              {serie.state.status === 'error' ? (
                <ErrorPanel title="Não foi possível carregar a tendência diária" error={serie.state.error} onRetry={serie.reload} />
              ) : null}
              {serie.state.status === 'success' ? (
                <TrendChart
                  title="Tendência diária"
                  description="Carrinhos abandonados e carrinhos recuperados por dia, no fuso da sua loja."
                  points={serie.state.data.points}
                />
              ) : null}
            </div>
            <div>
              {metricas.state.status === 'loading' ? <div aria-hidden="true" className="h-96 animate-pulse rounded-2xl border border-line bg-surface" /> : null}
              {metricas.state.status === 'error' ? (
                <ErrorPanel title="Não foi possível carregar o funil de conversão" error={metricas.state.error} onRetry={metricas.reload} />
              ) : null}
              {metricas.state.status === 'success' ? (
                <FunnelChart
                  title="Funil de conversão"
                  description="Do carrinho abandonado ao pagamento aprovado. Cada etapa é um subconjunto da anterior."
                  stages={etapasDoFunil(metricas.state.data)}
                />
              ) : null}
            </div>
          </div>
        </>
      )}

      <AtividadeRecente state={recentes.state} onRetry={recentes.reload} />

      {periodo === null ? null : (
        <p className="text-xs text-ink-subtle">
          Período de {formatDate(periodo.inicio)} a {formatDate(periodo.fim)} · {company.name}
        </p>
      )}
    </section>
  );
}

function etapasDoFunil(metrics: DashboardMetrics): readonly FunnelStageView[] {
  return [
    { key: 'abandoned', label: 'Carrinhos abandonados', value: metrics.abandonedCount, hint: 'Checkouts que ficaram 30 min sem pagamento' },
    { key: 'calls', label: 'Ligações disparadas', value: metrics.voiceCalls, hint: 'Tier 1 acionado depois da janela de 30 minutos' },
    { key: 'answered', label: 'Ligações atendidas', value: metrics.voiceAnswered, hint: 'Chamadas em que o lead atendeu e conversou com a IA' },
    { key: 'converted', label: 'Carrinhos recuperados', value: metrics.recoveredCount, hint: 'Pagamento aprovado em até 7 dias após um contato, creditado ao último contato' },
  ];
}
